import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { Sparkles, FileText, Mail, Download, CheckCircle2, Star, ArrowRight, Target, TrendingUp, Zap, Play, Briefcase, MessageSquare } from "lucide-react";

import { ATSExplainer } from "@/components/marketing/ATSExplainer";
import { CompetitorComparison } from "@/components/landing/CompetitorComparison";
import { MegaMenuNav } from "@/components/MegaMenuNav";
import { Footer } from "@/components/Footer";
import { Helmet } from "react-helmet-async";
import atsScoreMockup from "@/assets/ats-score-mockup.png";
import keywordAnalysisMockup from "@/assets/keyword-analysis-mockup.png";
import jobAnalyzerMockup from "@/assets/job-analyzer-mockup.png";

const Home = () => {
  
  
  return (
    <>
      <Helmet>
        <title>LoopBaan.ai - AI Resume Builder & ATS Optimizer | Land Your Dream Job Faster</title>
        <meta 
          name="description" 
          content="Create ATS-optimized resumes with AI. Check your ATS score, optimize keywords, and analyze job descriptions. AI-powered tools trusted by 10,000+ job seekers." 
        />
        <meta name="keywords" content="ATS resume checker, AI resume builder, resume optimization, keyword matching, job description analyzer, cover letter generator" />
        <link rel="canonical" href={window.location.origin} />
        <meta property="og:title" content="LoopBaan.ai - AI Resume Builder & ATS Optimizer" />
        <meta property="og:description" content="Beat applicant tracking systems with AI-powered resume optimization. Get your ATS score, keyword analysis, and personalized career tools." />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="min-h-screen bg-background">
      {/* Navigation */}
      <MegaMenuNav />

      {/* Hero Section */}
      <section className="relative overflow-hidden py-24 md:py-32">
        <div className="absolute inset-0 bg-[image:var(--gradient-hero)]" />
        <div className="absolute inset-0 bg-[image:var(--gradient-glow)]" />
        <div className="container relative mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center max-w-7xl mx-auto">
            {/* Left Column - Text Content */}
            <div className="text-left space-y-8">
              <div>
                <h1 className="mb-6 text-4xl font-bold leading-tight tracking-tight md:text-6xl lg:text-7xl">
                  <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                    Your AI Career
                  </span>
                  <br />
                  <span className="text-foreground">
                    Copilot
                  </span>
                </h1>
                <p className="mb-6 text-xl font-medium text-foreground/80 md:text-2xl">
                  Automate your entire job search—from applications to interviews to offer negotiations.
                </p>
                <p className="mb-8 text-base text-muted-foreground">
                  AI that applies to jobs for you, prepares you for interviews, and optimizes every aspect of your career journey.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/signup">
                  <Button size="lg" className="gap-2 text-base px-8 py-6 shadow-lg hover:shadow-xl transition-all duration-300 bg-gradient-to-r from-primary to-primary-glow w-full sm:w-auto">
                    Get Started <ArrowRight className="h-5 w-5" />
                  </Button>
                </Link>
              </div>

              {/* Social Proof */}
              <div className="pt-4">
                <div className="flex items-center gap-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                  ))}
                  <span className="ml-2 text-sm font-medium text-muted-foreground">4.7 out of 5 on Trustpilot</span>
                </div>
              </div>
            </div>

            {/* Right Column - Video Player with Floating Icons */}
            <div className="relative">
              <div className="relative aspect-video rounded-2xl overflow-hidden bg-gradient-to-br from-primary/10 to-accent/10 border-2 border-primary/20 shadow-2xl">
                <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary/5 to-accent/5">
                  <div className="group relative cursor-default">
                    <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl animate-pulse" />
                    <div className="relative w-24 h-24 md:w-32 md:h-32 rounded-full bg-gradient-to-br from-primary to-primary-glow flex items-center justify-center shadow-2xl">
                      <Play className="h-10 w-10 md:h-12 md:w-12 text-primary-foreground ml-2" fill="currentColor" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Icons - Symmetrically Organized */}
              <>
                {/* Top Row */}
                  <div className="absolute -top-8 left-1/4 w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10 backdrop-blur-sm border border-primary/30 flex items-center justify-center shadow-lg animate-float">
                    <FileText className="h-7 w-7 text-primary" />
                  </div>
                  
                  <div className="absolute -top-8 right-1/4 w-16 h-16 rounded-2xl bg-gradient-to-br from-accent/20 to-accent/10 backdrop-blur-sm border border-accent/30 flex items-center justify-center shadow-lg animate-float-delayed">
                    <Mail className="h-7 w-7 text-accent" />
                  </div>
                  
                  {/* Middle Row */}
                  <div className="absolute top-1/3 -left-6 w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10 backdrop-blur-sm border border-primary/30 flex items-center justify-center shadow-lg animate-float">
                    <Target className="h-6 w-6 text-primary" />
                  </div>
                  
                  <div className="absolute top-1/3 -right-6 w-14 h-14 rounded-2xl bg-gradient-to-br from-accent/20 to-accent/10 backdrop-blur-sm border border-accent/30 flex items-center justify-center shadow-lg animate-float-delayed">
                    <MessageSquare className="h-6 w-6 text-accent" />
                  </div>
                  
                  {/* Bottom Row */}
                  <div className="absolute -bottom-6 left-1/4 w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10 backdrop-blur-sm border border-primary/30 flex items-center justify-center shadow-lg animate-float-delayed">
                    <Briefcase className="h-7 w-7 text-primary" />
                  </div>
                  
                  <div className="absolute -bottom-6 right-1/4 w-16 h-16 rounded-2xl bg-gradient-to-br from-accent/20 to-accent/10 backdrop-blur-sm border border-accent/30 flex items-center justify-center shadow-lg animate-float">
                    <CheckCircle2 className="h-7 w-7 text-accent" />
                  </div>
              </>
            </div>
          </div>

          {/* Stats Below Hero */}
          <div className="mt-20 max-w-7xl mx-auto">
            <div className="flex flex-wrap items-center justify-center gap-12 text-center">
              <div>
                <div className="text-4xl font-bold text-primary">1M+</div>
                <div className="text-sm text-muted-foreground">Jobs Searched</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-primary">50K+</div>
                <div className="text-sm text-muted-foreground">Applications Submitted</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-primary">91%</div>
                <div className="text-sm text-muted-foreground">Avg ATS Score</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Overview */}
      <section className="py-32 bg-gradient-to-b from-background via-muted/10 to-background">
        <div className="container mx-auto px-4">
          <div className="mb-20 text-center max-w-4xl mx-auto">
            <h2 className="mb-6 text-5xl font-bold bg-gradient-to-br from-foreground to-foreground/70 bg-clip-text text-transparent">
              The Complete Career Automation Platform
            </h2>
            <p className="text-xl text-muted-foreground leading-relaxed">
              From AI-powered applications to interview prep and salary negotiations—everything you need to land your dream job, faster.
            </p>
          </div>

          {/* Feature 1: AI Smart Apply */}
          <div className="mb-32 grid gap-12 lg:grid-cols-2 lg:gap-16 items-center">
            <div className="order-2 lg:order-1">
              <Badge className="mb-6 bg-primary/10 text-primary border-primary/20">AI SMART APPLY</Badge>
              <h2 className="mb-6 text-4xl md:text-5xl font-bold leading-tight">
                Apply to 100+ Jobs{" "}
                <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                  While You Sleep
                </span>
              </h2>
              <p className="mb-8 text-xl text-muted-foreground leading-relaxed">
                Our AI agent searches jobs, tailors your resume and cover letter for each position, and submits applications automatically. Set your preferences once, then let the platform work for you 24/7.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-semibold text-foreground">Search 1M+ jobs automatically</span>
                    <p className="text-muted-foreground">Access jobs from Indeed, LinkedIn, ZipRecruiter, and more</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-semibold text-foreground">AI-tailored applications</span>
                    <p className="text-muted-foreground">Custom resume and cover letter for every job</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-semibold text-foreground">Set daily application limits</span>
                    <p className="text-muted-foreground">Control your application rate and quality</p>
                  </div>
                </li>
              </ul>
              <Link to="/signup">
                <Button size="lg" className="gap-2 shadow-lg hover:shadow-xl transition-all">
                  Start Smart Apply <ArrowRight className="h-5 w-5" />
                </Button>
              </Link>
            </div>
            <div className="order-1 lg:order-2">
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-primary to-accent rounded-2xl blur-2xl opacity-20 group-hover:opacity-30 transition-opacity" />
                <Card className="relative p-8 bg-gradient-to-br from-card to-card/50 border-primary/20">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-lg font-semibold">Application Queue</h3>
                      <Badge className="bg-primary/20 text-primary">Automated</Badge>
                    </div>
                    {[
                      { company: "Google", role: "Senior Engineer", status: "Applying" },
                      { company: "Meta", role: "Product Manager", status: "Success" },
                      { company: "Amazon", role: "SWE II", status: "In Queue" },
                      { company: "Netflix", role: "Data Scientist", status: "In Queue" },
                    ].map((job, i) => (
                      <div key={i} className="flex items-center justify-between p-4 bg-primary/5 rounded-lg border border-primary/20">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center">
                            <Sparkles className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">{job.company}</p>
                            <p className="text-sm text-muted-foreground">{job.role}</p>
                          </div>
                        </div>
                        <Badge variant={job.status === "Success" ? "default" : "secondary"} className="text-xs">
                          {job.status}
                        </Badge>
                      </div>
                    ))}
                    <div className="mt-6 pt-4 border-t flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Daily Limit: 25</span>
                      <span className="font-semibold">Applied: 12/25</span>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>

          {/* Feature 2: AI Interview Prep */}
          <div className="mb-32 grid gap-12 lg:grid-cols-2 lg:gap-16 items-center">
            <div className="order-1">
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-accent to-primary rounded-2xl blur-2xl opacity-20 group-hover:opacity-30 transition-opacity" />
                <Card className="relative p-8 bg-gradient-to-br from-card to-card/50 border-accent/20">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="h-12 w-12 rounded-full bg-accent/20 flex items-center justify-center">
                      <Sparkles className="h-6 w-6 text-accent" />
                    </div>
                    <div>
                      <div className="font-semibold">AI Interview Coach</div>
                      <div className="text-sm text-muted-foreground">Real-time practice session</div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="p-4 bg-accent/5 rounded-lg border border-accent/20">
                      <p className="text-sm font-medium mb-2">AI: "Tell me about a time you led a difficult project."</p>
                      <p className="text-xs text-muted-foreground">💬 Voice or text response...</p>
                    </div>
                    <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                      <p className="text-sm font-medium mb-2">You: "At my previous role, I led a team of 5..."</p>
                      <div className="flex gap-2 mt-2">
                        <Badge className="text-xs bg-primary/20">Clear Structure</Badge>
                        <Badge className="text-xs bg-primary/20">Good Examples</Badge>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
            <div className="order-2">
              <Badge className="mb-6 bg-accent/10 text-accent border-accent/20">AI INTERVIEW PREP</Badge>
              <h2 className="mb-6 text-4xl md:text-5xl font-bold leading-tight">
                Practice Interviews{" "}
                <span className="bg-gradient-to-r from-accent to-primary bg-clip-text text-transparent">
                  With AI
                </span>
              </h2>
              <p className="mb-8 text-xl text-muted-foreground leading-relaxed">
                Conversational AI that acts like a real interviewer. Get instant feedback, practice with your voice, and build confidence before the real thing.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-semibold text-foreground">Voice or text practice</span>
                    <p className="text-muted-foreground">Practice speaking or typing your answers</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-semibold text-foreground">Real-time feedback</span>
                    <p className="text-muted-foreground">AI analyzes your answers and gives suggestions</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-semibold text-foreground">Company-specific questions</span>
                    <p className="text-muted-foreground">Practice questions tailored to your target role</p>
                  </div>
                </li>
              </ul>
              <Link to="/signup">
                <Button size="lg" variant="outline" className="gap-2 border-2 hover:border-accent transition-all">
                  Start Practicing <ArrowRight className="h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Feature 3: ATS & Application Tracking */}
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-center">
            <div className="order-2 lg:order-1">
              <Badge className="mb-6 bg-primary/10 text-primary border-primary/20">ATS OPTIMIZATION & TRACKING</Badge>
              <h2 className="mb-6 text-4xl md:text-5xl font-bold leading-tight">
                Beat ATS Systems &{" "}
                <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                  Track Everything
                </span>
              </h2>
              <p className="mb-8 text-xl text-muted-foreground leading-relaxed">
                Get a 91% ATS score with keyword optimization, formatting checks, and real-time analysis. Plus, track all your applications in one dashboard with status updates and follow-up reminders.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <TrendingUp className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-semibold text-foreground">ATS compatibility score</span>
                    <p className="text-muted-foreground">Know if your resume will pass automated filters</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <TrendingUp className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-semibold text-foreground">Application tracker</span>
                    <p className="text-muted-foreground">Manage all applications with status and notes</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <TrendingUp className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-semibold text-foreground">Analytics dashboard</span>
                    <p className="text-muted-foreground">Track response rates and interview success</p>
                  </div>
                </li>
              </ul>
              <Link to="/signup">
                <Button size="lg" className="gap-2 shadow-lg hover:shadow-xl transition-all">
                  Check ATS Score <ArrowRight className="h-5 w-5" />
                </Button>
              </Link>
            </div>
            <div className="order-1 lg:order-2">
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-primary to-accent rounded-2xl blur-2xl opacity-20 group-hover:opacity-30 transition-opacity" />
                <Card className="relative p-8 bg-gradient-to-br from-card to-card/50 border-primary/20">
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold">ATS Score</h3>
                      <div className="text-4xl font-bold text-primary">91%</div>
                    </div>
                    <div className="space-y-3">
                      {[
                        { label: "Keywords", score: 95, color: "bg-primary" },
                        { label: "Formatting", score: 88, color: "bg-accent" },
                        { label: "Experience", score: 90, color: "bg-secondary" },
                      ].map((item, i) => (
                        <div key={i}>
                          <div className="flex justify-between text-sm mb-1">
                            <span>{item.label}</span>
                            <span className="font-medium">{item.score}%</span>
                          </div>
                          <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <div className={`h-full ${item.color}`} style={{ width: `${item.score}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="pt-4 border-t space-y-3">
                      <h4 className="font-semibold text-sm">Recent Applications</h4>
                      {[
                        { company: "Google", status: "Interview", date: "2d ago" },
                        { company: "Meta", status: "Applied", date: "1w ago" },
                        { company: "Amazon", status: "Offered", date: "3d ago" },
                      ].map((app, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                          <div>
                            <p className="font-medium text-sm">{app.company}</p>
                            <p className="text-xs text-muted-foreground">{app.date}</p>
                          </div>
                          <Badge variant="secondary" className="text-xs">
                            {app.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="border-t py-24 bg-gradient-to-b from-background to-muted/20">
        <div className="container mx-auto px-4">
          <div className="mb-20 text-center">
            <h2 className="mb-4 text-5xl font-bold bg-gradient-to-br from-foreground to-foreground/70 bg-clip-text text-transparent">How It Works</h2>
            <p className="text-xl text-muted-foreground">Your AI career copilot in three simple steps</p>
          </div>
          
          <div className="grid gap-8 md:grid-cols-3 max-w-6xl mx-auto">
            <Card className="group p-8 text-center transition-all duration-300 hover:shadow-[var(--shadow-xl)] hover:-translate-y-2 border-2 hover:border-primary/50 bg-gradient-to-b from-card to-card/50">
              <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 group-hover:from-primary/30 group-hover:to-primary/10 transition-all duration-300 group-hover:scale-110">
                <FileText className="h-10 w-10 text-primary" />
              </div>
              <h3 className="mb-4 text-2xl font-bold">1. Set Your Preferences</h3>
              <p className="text-muted-foreground leading-relaxed">
                Tell us your skills, target roles, salary expectations, and work preferences. Our AI learns what you want.
              </p>
            </Card>

            <Card className="group p-8 text-center transition-all duration-300 hover:shadow-[var(--shadow-xl)] hover:-translate-y-2 border-2 hover:border-primary/50 bg-gradient-to-b from-card to-card/50">
              <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-accent/20 to-accent/5 group-hover:from-accent/30 group-hover:to-accent/10 transition-all duration-300 group-hover:scale-110">
                <Sparkles className="h-10 w-10 text-accent" />
              </div>
              <h3 className="mb-4 text-2xl font-bold">2. AI Applies For You</h3>
              <p className="text-muted-foreground leading-relaxed">
                Searches 1M+ jobs, customizes your resume and cover letter for each, and submits applications automatically—all while you sleep.
              </p>
            </Card>

            <Card className="group p-8 text-center transition-all duration-300 hover:shadow-[var(--shadow-xl)] hover:-translate-y-2 border-2 hover:border-primary/50 bg-gradient-to-b from-card to-card/50">
              <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-green-500/20 to-green-500/5 group-hover:from-green-500/30 group-hover:to-green-500/10 transition-all duration-300 group-hover:scale-110">
                <Zap className="h-10 w-10 text-green-500" fill="currentColor" />
              </div>
              <h3 className="mb-4 text-2xl font-bold">3. Ace Interviews & Get Offers</h3>
              <p className="text-muted-foreground leading-relaxed">
                Practice with AI, track responses, and negotiate offers. Our platform guides you from application to acceptance.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* ATS Explainer Section */}
      <ATSExplainer />

      {/* Competitor Comparison - Replacing Pricing */}
      <CompetitorComparison />

      {/* Features */}
      <section className="border-t py-20">
...
      </section>

      {/* FAQ */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="mb-16 text-center">
            <h2 className="mb-4 text-4xl font-bold">Frequently Asked Questions</h2>
          </div>
          
          <div className="mx-auto max-w-3xl space-y-6">
            {[
              {
                q: "Will this pass ATS?",
                a: "Yes—your resume uses relevant keywords and clean formatting designed to pass applicant tracking systems."
              },
              {
                q: "Who owns my documents?",
                a: "You do. Download and reuse anytime. We never claim ownership of your content."
              },
              {
                q: "Can I edit the AI-generated content?",
                a: "Absolutely! All generated content is fully editable. The AI gives you a strong foundation to build on."
              }
            ].map((faq, i) => (
              <Card key={i} className="p-6">
                <h3 className="mb-2 text-lg font-semibold">{faq.q}</h3>
                <p className="text-muted-foreground">{faq.a}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
      </div>
    </>
  );
};

export default Home;